// Chat.tsx
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  KeyboardEvent,
} from "react";
import styled from "styled-components";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";

const API_URL = "https://server.brownspectator.com";

// ---- Styled Components ----

const FullPageContainer = styled.div`
  height: 100%;
  width: 100%;
  background-color: #1f1f1f;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ChatContainer = styled.div`
  width: 100%;
  max-width: 600px;
`;

const TargetHeader = styled.button`
  display: block;
  margin: 0 auto 4px;
  background: none;
  border: none;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
  cursor: pointer;
`;

const ChangeTargetLine = styled.div`
  font-size: 14px;
  color: #fff;
  margin: 0 auto 20px;
  text-align: center;
`;

const ChangeTargetLink = styled(Link)`
  text-decoration: underline;
  color: inherit;
`;

const InputWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px;
  background: transparent;
  display: flex;
  flex-direction: column;
`;

const ReadOnlyContent = styled.div`
  min-height: 80px;
  font-size: 16px;
  color: #fff;
  font-family: inherit;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const InlineEmailInput = styled.input<{ disabled?: boolean }>`
  font-size: 16px;
  font-family: inherit;
  color: ${({ disabled }) => (disabled ? "#aaa" : "#fff")};
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ disabled }) => (disabled ? "#888" : "#2ecc71")};
  width: 300px;
  background: ${({ disabled }) => (disabled ? "#222" : "none")};
  outline: none;
  &:focus {
    box-shadow: 0 0 10px 4px #2ecc7166;
  }
`;

const RectangularSubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #2ecc71;
  color: #1f1f1f;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 12px;
  &:hover:not(:disabled) {
    box-shadow: 0 0 10px 4px #2ecc7166;
  }
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
  }
`;

const EmailPopupContainer = styled.div`
  position: relative;
  background-color: #333;
  border: 1px solid #555;
  border-radius: 8px;
  padding: 12px;
  margin-top: 6px;
  color: #fff;
  font-size: 14px;
`;

// ---- Component ----

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const query = (location.state as { query?: string })?.query || "";

  const [emailInput, setEmailInput] = useState(
    () => localStorage.getItem("contactEmail") || ""
  );
  const [forwardEmail, setForwardEmail] = useState(
    () => localStorage.getItem("personalEmail") || ""
  );
  const [isAnonymous, setIsAnonymous] = useState(
    () => localStorage.getItem("isAnonymous") === "true"
  );
  const [needsEmail, setNeedsEmail] = useState(false);
  const [headerText, setHeaderText] = useState("Generating...");
  const [content, setContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);

  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const contactEmailRef = useRef<HTMLInputElement>(null);
  const forwardEmailRef = useRef<HTMLInputElement>(null);

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  // persist emails & anonymity
  useEffect(() => {
    localStorage.setItem("contactEmail", emailInput);
  }, [emailInput]);
  useEffect(() => {
    localStorage.setItem("personalEmail", forwardEmail);
  }, [forwardEmail]);
  useEffect(() => {
    localStorage.setItem("isAnonymous", isAnonymous ? "true" : "false");
  }, [isAnonymous]);

  const fetchChunks = async (prompt: string) => {
    if (!prompt) return;
    setIsStreaming(true);
    let firstChunk = true;
    let acc = "";
    try {
      const res = await fetch(`${API_URL}/chat/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok || !res.body) throw new Error("Bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          const chunk = dec
            .decode(value, { stream: !done })
            .replace(/\[BEGIN_REWRITE\]/g, "")
            .replace(/\[END_REWRITE\]/g, "");
          if (firstChunk) {
            setHeaderText(chunk);
            firstChunk = false;
          } else {
            acc += chunk;
            setContent(acc);
          }
        }
      }
      if (acc.includes("[Email Address]")) {
        setNeedsEmail(true);
      }
    } catch (err) {
      console.error(err);
    }
    setIsStreaming(false);
  };

  // scroll to bottom on each content update
  useLayoutEffect(() => {
    if (inputWrapperRef.current) {
      inputWrapperRef.current.scrollTop = inputWrapperRef.current.scrollHeight;
    }
  }, [content]);

  // --- Updated: post submission navigates with institution param ---
  const postSubmission = async () => {
    const finalMessage = content.replace(
      /\[Email Address\]/g,
      emailInput.trim()
    );
    const payload = {
      institution: headerText,
      message: finalMessage,
      contact_email: emailInput,
      personal_email: isAnonymous ? forwardEmail : emailInput,
      timestamp: new Date().toISOString(),
    };

    // OPTION A: fetch with keepalive
    fetch(`${API_URL}/chat/submit/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true, // <-- tells the browser to finish it even if page unloads
    }).catch(console.error);

    // OPTION B: sendBeacon
    // const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    // navigator.sendBeacon(`${API_URL}/chat/submit/`, blob);
    // navigate to root with institution query param
    navigate(`/?institution=${encodeURIComponent(headerText)}`);
  };
  // --- end updated ---

  // on new query
  useEffect(() => {
    setHeaderText("Generating...");
    setContent("");
    setNeedsEmail(false);
    fetchChunks(query);
  }, [query]);

  const handleSend = async () => {
    if (isStreaming) return;
    await postSubmission();
  };

  const handleEmailSubmit = () => {
    if (isAnonymous) {
      if (!isValidEmail(forwardEmail)) return;
      localStorage.setItem("contactEmail", "records@trialhouse.com");
      localStorage.setItem("personalEmail", forwardEmail);
    } else {
      if (!isValidEmail(emailInput)) return;
      localStorage.setItem("contactEmail", emailInput);
      localStorage.setItem("personalEmail", emailInput);
    }
    postSubmission();
  };

  const renderEmailPopup = () => (
    <EmailPopupContainer>
      <div>Enter the email responses should be sent to.</div>
      <div style={{ marginTop: "8px" }}>
        <InlineEmailInput
          ref={contactEmailRef}
          value={emailInput}
          onChange={(e) => {
            if (!isAnonymous) setEmailInput(e.target.value);
          }}
          placeholder="you@example.com"
          onKeyDown={(e) => {
            if (!isAnonymous && e.key === "Enter" && isValidEmail(emailInput)) {
              e.preventDefault();
              handleEmailSubmit();
            }
          }}
          autoFocus
          disabled={isAnonymous}
        />
      </div>
      <div style={{ marginTop: "8px" }}>
        <label style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={() => {
              if (!isAnonymous) {
                setIsAnonymous(true);
                setForwardEmail(emailInput);
                setEmailInput("responses@trialhouse.com");
                setTimeout(() => {
                  forwardEmailRef.current?.focus();
                }, 0);
              } else {
                setIsAnonymous(false);
                setEmailInput(forwardEmail);
                setForwardEmail("");
                setTimeout(() => {
                  contactEmailRef.current?.focus();
                }, 0);
              }
            }}
            style={{ marginRight: "4px" }}
          />
          Remain anonymous
        </label>
      </div>
      {isAnonymous && (
        <div style={{ marginTop: "8px" }}>
          <div style={{ marginBottom: "4px" }}>
            We'll give the institution a generic email address. Where should we
            forward the responses?
          </div>
          <InlineEmailInput
            ref={forwardEmailRef}
            value={forwardEmail}
            onChange={(e) => setForwardEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>
      )}
    </EmailPopupContainer>
  );

  const renderInlineEmail = () => {
    const lines = content.split("\n");
    return lines.map((line, lineIndex) => (
      <React.Fragment key={lineIndex}>
        {line.split(/(?<=[.!?])\s+/).map((sentence, idx) => {
          if (sentence.includes("[Email Address]")) {
            const parts = sentence.split("[Email Address]");
            const emailReplacement =
              emailInput.trim() !== "" ? (
                <span style={{ color: "#2ecc71", textDecoration: "underline" }}>
                  {emailInput}
                </span>
              ) : (
                <span style={{ color: "#aaa", textDecoration: "underline" }}>
                  you@example.com
                </span>
              );
            return (
              <React.Fragment key={idx}>
                {parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {part}
                    {i !== parts.length - 1 && emailReplacement}
                  </React.Fragment>
                ))}
                {renderEmailPopup()}
              </React.Fragment>
            );
          } else {
            return <React.Fragment key={idx}>{sentence} </React.Fragment>;
          }
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const emailValid =
    !needsEmail ||
    (isAnonymous ? isValidEmail(forwardEmail) : isValidEmail(emailInput));
  const isEmpty = !content.trim();
  const sendDisabled = isStreaming || isEmpty || !emailValid;

  return (
    <FullPageContainer>
      <ChatContainer>
        <TargetHeader>{headerText}</TargetHeader>
        <ChangeTargetLine>
          (
          <ChangeTargetLink to="/" state={{ openChat: true }}>
            Change target
          </ChangeTargetLink>
          )
        </ChangeTargetLine>
        <InputWrapper ref={inputWrapperRef}>
          <ReadOnlyContent>
            {needsEmail ? renderInlineEmail() : content}
          </ReadOnlyContent>
        </InputWrapper>
        <RectangularSubmitButton onClick={handleSend} disabled={sendDisabled}>
          Tell DEI officers to cease and desist
        </RectangularSubmitButton>
      </ChatContainer>
    </FullPageContainer>
  );
};

export default Chat;
